import styled from 'styled-components';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import { StampPreview } from '@dealstack/frontend/shared/components/StampPreview/StampPreview.tsx';
import type { SignatureSlotDisplay } from '@dealstack/frontend/shared/components/DocumentEditor/extensions/signatureNode';
import { ANCHORED_STAMP_WIDTH_PT } from '@dealstack/shared/lib/common/business/stampGeometry';

import { pdfToBrowser } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/placement.utils.ts';
import type {
	PreviewDateBoxRes,
	PreviewSignatureBoxRes,
} from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';

interface Props {
	/** The signature boxes on THIS page, in PDF points. */
	boxes: PreviewSignatureBoxRes[];
	dateBoxes: PreviewDateBoxRes[];
	/** The page's own height in points, for the y-flip. */
	pageHeightPt: number;
	/** CSS pixels per PDF point, as this page was rendered. */
	scale: number;
	/** Label + role per slot key, from the template's signing roles. */
	slotDisplay: Record<string, SignatureSlotDisplay>;
	showMetadata: boolean;
}

/**
 * The stamps and date boxes a rendered page will carry, drawn over the space reserved for them.
 *
 * The render leaves each signature box EMPTY on purpose — a stamp is a raster the signing path
 * composites on at sign time — so a preview of the bytes alone shows an unexplained gap down the
 * page, which is the one thing a preview must not do.
 *
 * The same `StampPreview` the editor's anchor draws, so the two views of the same document
 * cannot disagree about what is going there. Positioned from the render's own coordinates rather
 * than re-derived here: only the render knows where a box ended up for this recipient.
 */
export const PreviewStampOverlay = ({
	boxes,
	dateBoxes,
	pageHeightPt,
	scale,
	slotDisplay,
	showMetadata,
}: Props) => (
	// Deliberately not `docx-preview-stamp-…`: that is the prefix each stamp's own id carries,
	// and an overlay sharing it makes every `^=` selector match the whole page too.
	<Overlay data-testid="docx-preview-stamps">
		{boxes.map((box) => {
			const slotKey = signatureSlotKey(box);
			const display = slotDisplay[slotKey];
			const browserBox = pdfToBrowser(box, pageHeightPt, scale);
			return (
				<Box
					key={slotKey}
					$left={browserBox.left}
					$top={browserBox.top}
					$width={browserBox.width}
					$height={browserBox.height}
					data-testid={`docx-preview-stamp-${slotKey}`}
				>
					<StampPreview
						// A role since deleted leaves the document holding a slot nothing
						// describes. Named as such rather than omitted, so what is on the page
						// matches the anchor the editor shows for it.
						slotLabel={display?.label ?? 'Unassigned signature'}
						isWitness={box.isWitness}
						roleType={display?.roleType ?? DocumentSigningRoleType.PARTY}
						kind="signature"
						showMetadata={showMetadata}
						// The stamp's own type scales off its drawn width against the design
						// width, exactly as the placement canvas does it — clamped so a zoomed
						// page does not blow the type up past the border.
						scale={Math.min(
							2,
							Math.max(0.3, browserBox.width / ANCHORED_STAMP_WIDTH_PT)
						)}
					/>
				</Box>
			);
		})}
		{dateBoxes.map((box) => {
			const browserBox = pdfToBrowser(box, pageHeightPt, scale);
			return (
				<Box
					key={box.templateDatePlacementId}
					$left={browserBox.left}
					$top={browserBox.top}
					$width={browserBox.width}
					$height={browserBox.height}
					data-testid={`docx-preview-date-${box.templateDatePlacementId}`}
				>
					<StampPreview
						slotLabel=""
						isWitness={false}
						roleType={DocumentSigningRoleType.PARTY}
						kind="date"
						showMetadata={showMetadata}
						scale={Math.min(
							2,
							Math.max(0.3, browserBox.width / ANCHORED_STAMP_WIDTH_PT)
						)}
					/>
				</Box>
			);
		})}
	</Overlay>
);

const Overlay = styled.div`
	position: absolute;
	inset: 0;
	// Read-only: every click belongs to the page underneath.
	pointer-events: none;
`;

const Box = styled.div<{ $left: number; $top: number; $width: number; $height: number }>`
	position: absolute;
	left: ${({ $left }) => $left}px;
	top: ${({ $top }) => $top}px;
	width: ${({ $width }) => $width}px;
	height: ${({ $height }) => $height}px;
`;
